import { Dialog, DialogContent, DialogSurface, DialogTitle, DialogActions, Dropdown, Option, RadioGroup, Radio, Text, Field, Label, SearchBox, Button } from "@fluentui/react-components";
import { ContractField, Question, Tendering, TenderingPart } from "../../models/tendering";
import { FC, useEffect, useState, useMemo } from "react";
import { set } from "react-hook-form";
import { DismissRegular } from "@fluentui/react-icons";

interface LinkQuestionsDialogProps {
  onClose: () => void;
  open: boolean;
  tendering: Tendering;
  field: ContractField;
  onQuestionLinked: (field: ContractField, question: Question | null) => void; // New callback prop
}

export const LinkQuestionsDialog: FC<LinkQuestionsDialogProps> = ({
  onClose,
  open,
  tendering,
  field,
  onQuestionLinked
}) => {
  const [selectedTenderingPart, setSelectedTenderingPart] = useState<TenderingPart>();
  const [questions, setQuestions] = useState<Question[]>([]);
  const [selectedQuestion, setSelectedQuestion] = useState<Question | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>("");

  // Filter questions based on search query
  const filteredQuestions = useMemo(() => {
    if (!searchQuery.trim()) return questions;
    
    return questions.filter(question => 
      (question.text || "").toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [questions, searchQuery]);

  useEffect(() => {
    if(field.questionID){
      const selectedQuestion = questions.find(q => q.questionID === field.questionID);
      setSelectedQuestion(selectedQuestion || null);
    }
  }, [questions]);

  useEffect(() => {
    if(selectedTenderingPart){
      setQuestions(selectedTenderingPart.questions || []);
      setSelectedQuestion(null); // Reset selected question when changing part
      setSearchQuery(""); // Reset search when changing part
    }
  }, [selectedTenderingPart]);

  const handleQuestionSelect = (questionIndex: string) => {
    const index = parseInt(questionIndex);
    const selectedFromFiltered = filteredQuestions[index];
    setSelectedQuestion(selectedFromFiltered || null);
  };

  const getQuestionIndex = (question: Question) => {
    return filteredQuestions.indexOf(question);
  };

  const handleLinkQuestion = () => {
    onQuestionLinked(field, selectedQuestion);
    onClose();

  };

  const clearSelection = () => {
    setSelectedQuestion(null);
  };

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(_event, data) => !data.open && onClose()}
        modalType="alert"
      >
        <DialogSurface style={{ minWidth: '500px', maxWidth: '700px' }}>
          <DialogTitle            
          action={
            <Button
              appearance="subtle"
              shape="circular"
              aria-label="close"
              icon={<DismissRegular />}
              onClick={onClose}
            />
          }>
            Länka fråga till <strong>{field.fieldName}</strong>
          </DialogTitle>
          <DialogContent>
            <div className="space-y-6 mb-5">
              <Field>
                <Label weight="semibold">Välj delområde</Label>
                <Dropdown
                  placeholder="Välj delområde"
                  value={selectedTenderingPart?.tenderingPartID.toString() || ""}
                  onOptionSelect={(_, data) => {
                    const part = tendering.tenderingParts.find(p => p.tenderingPartID.toString() === data.optionValue);
                    setSelectedTenderingPart(part);
                  }}
                  style={{ width: '100%' }}
                >
                  {tendering.tenderingParts.map((part) => (
                    <Option
                      key={part.tenderingPartID}
                      value={part.tenderingPartID.toString()}
                    >
                      {part.tenderingPart}
                    </Option>
                  ))}
                </Dropdown>
              </Field>

              {questions.length > 0 && (
                <Field>
                  <div className="flex justify-between items-center mb-2">
                    <Label weight="semibold">
                      Välj fråga att länka
                      <Text size={200} className="text-gray-500 ml-2">
                        ({questions.length} frågor totalt)
                      </Text>
                    </Label>
                    {selectedQuestion && (
                      <Button
                        appearance="secondary"
                        size="small"
                        onClick={clearSelection}
                        className="text-gray-600"
                      >
                        Rensa val
                      </Button>
                    )}
                  </div>
                  
                  {/* Search bar with better spacing */}
                  <div className="mb-4">
                    <SearchBox
                      placeholder="Sök bland frågor..."
                      value={searchQuery}
                      onChange={(_, data) => setSearchQuery(data.value)}
                      style={{ width: '100%' }}
                    />
                  </div>
                  
                  {/* Questions container with improved styling */}
                  <div className="border border-gray-200 rounded-lg bg-gray-50 max-h-96 overflow-y-auto">
                    {filteredQuestions.length > 0 ? (
                      <div className="p-3">
                        <RadioGroup
                          value={selectedQuestion && filteredQuestions.includes(selectedQuestion) 
                            ? getQuestionIndex(selectedQuestion).toString() 
                            : ""}
                          onChange={(_, data) => handleQuestionSelect(data.value)}
                        >
                          {filteredQuestions.map((question, index) => (
                            <div key={`${questions.indexOf(question)}-${index}`} className="w-full py-2 px-3 mb-2 bg-white rounded-md shadow-sm hover:bg-blue-50 transition-colors">
                              <Radio
                                value={index.toString()}
                                label={
                                  <div className="flex flex-col">
                                    <Text weight="medium">
                                      {question.text || `Fråga ${questions.indexOf(question) + 1}`}
                                    </Text>
                                    {question.heading && (
                                      <Text size={200} italic className="text-gray-600 mt-1">
                                        {question.heading}
                                      </Text>
                                    )}
                                  </div>
                                }
                              />
                            </div>
                          ))}
                        </RadioGroup>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center py-12 text-center">
                        <Text className="text-gray-500 text-lg mb-2">
                          🔍 Inga frågor hittades
                        </Text>
                        <Text size={300} className="text-gray-400">
                          {searchQuery ? `för "${searchQuery}"` : "Välj ett delområde först"}
                        </Text>
                      </div>
                    )}
                  </div>
                  
                  {/* Status indicators with better styling */}
                  <div className="flex justify-between items-center mt-3">
                    {searchQuery && filteredQuestions.length > 0 && (
                      <Text size={200} className="text-blue-600 font-medium">
                        📋 Visar {filteredQuestions.length} av {questions.length} frågor
                      </Text>
                    )}
                    {selectedQuestion && (
                      <div className="bg-green-50 border border-green-200 rounded-md p-2 flex-1 ml-4">
                        <Text size={200} className="text-green-700 font-medium">
                          ✓ Vald: {selectedQuestion.text || `Fråga ${questions.indexOf(selectedQuestion) + 1}`}
                        </Text>
                      </div>
                    )}
                  </div>
                </Field>
              )}
            </div>
          </DialogContent>
          
          {/* Add action buttons */}
          <DialogActions>
            <Button appearance="secondary" onClick={onClose}>
              Avbryt
            </Button>
            <Button 
              appearance="primary" 
              onClick={handleLinkQuestion}
            >
              Länka fråga
            </Button>
          </DialogActions>
        </DialogSurface>
      </Dialog>
    </>
  );
}

export default LinkQuestionsDialog;